import Bugsnag from "@bugsnag/js";
import mixpanel from "mixpanel-browser";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import { StringParam, useQueryParams } from "use-query-params";

import {
  fetchGauthCreds,
  loginFirebase,
  updateProfile,
} from "../Store/Actions";
import { setCountryCodes } from "../Store/Actions/countryCodesActions";
import {
  getAIFeatureFlag,
  setFeatureFlags,
} from "../Store/Actions/featureFlagsActions";
import axiosInstance, {
  getUserAuthTokenFromSecureStore,
  storeAuthTokensToSecureStore,
} from "../axiosInstance";
import { getPlatformType } from "../helperFunctions";
import useMobileView from "../hooks/useMobileView";
import useNewChatUnreadListener from "./useNewChatUnreadListener";
import URL from "../urls";

function RootRoute() {
  const dispatch = useDispatch();
  useNewChatUnreadListener();

  const isMobile = useMobileView();

  const { user, googleClientId } = useSelector(state => ({
    user: state.auth.user,
    googleClientId: state.auth.googleAuth?.clientId,
  }));

  const [{ platform, jwt_token }] = useQueryParams({
    platform: StringParam,
    jwt_token: StringParam,
  });

  useEffect(() => {
    localStorage.setItem("platform", getPlatformType(isMobile, platform));
  }, []);

  useEffect(() => {
    if (user) mixpanel.identify(user.firebaseid);

    if (user && !googleClientId && !jwt_token) dispatch(fetchGauthCreds());
  }, [user]);

  useEffect(() => {
    if (user && !jwt_token) {
      const userFirebaseId = user.firebaseid;
      Bugsnag.setUser(userFirebaseId);
      refreshAuthToken();
      getFeatureFlags();
      dispatch(getAIFeatureFlag());
      dispatch(setCountryCodes());
    }
  }, [user, jwt_token]);

  const refreshAuthToken = async () => {
    try {
      // Disable refresh token inside mobile app
      if (window.ReactNativeWebView) return;

      const tokenStr = await getUserAuthTokenFromSecureStore();
      const response = await axiosInstance.post(URL.tokenRefresh);
      await storeAuthTokensToSecureStore(response.headers);
      if (tokenStr) {
        const updatedUser = response?.data?.data;
        if (updatedUser) {
          const updatedFirebaseToken = updatedUser.fb_token;
          if (user?.fb_token !== updatedFirebaseToken) {
            loginFirebase({ fb_token: updatedFirebaseToken }, dispatch);
            dispatch(updateProfile(updatedUser));
          }
        }
      }
    } catch (err) {
      Bugsnag.notify(err);
    }
  };

  const getFeatureFlags = async () => {
    try {
      const response = await axiosInstance.get(URL.featureFlags, {
        params: { source: "twa" },
      });
      dispatch(setFeatureFlags(response?.data?.features));
    } catch (error) {
      Bugsnag.notify(error);
    }
  };

  return <Outlet />;
}

export default RootRoute;
